import { PathParams, rest } from 'msw'
import { DepositBankAccount } from 'types/BankAccountsDetails'
import { BaseErrorResponse } from 'types/beneficiaries'
import { Quote } from 'types/payments'
import { Transaction } from 'types/transaction'
import { Currency, CurrencyLimits, WalletType } from 'types/types'
import {
  CurrencyInterface,
  PayFee,
  WalletsPayRequest,
  WalletSummary,
  WalletsWithdrawValidateRequest,
  WalletsWithdrawValidateResponseError,
} from 'types/wallets'
import mockedSupportedCurrencies from '../../../__mocks__/Currency/supportedCurrencies.json'
import mockedTransactions from '../../../__mocks__/Transactions/transactions.json'
import eurSwiftPaymentMethods from '../../../__mocks__/Wallets/eurSwiftPaymentMethods.json'
import mockCsvExportTransactions from '../../../__mocks__/Wallets/exportCsvTransansactionResponse.json'
import mockPdfExportTransactions from '../../../__mocks__/Wallets/exportPdfTransansactionResponse.json'
import mockPay from '../../../__mocks__/Wallets/pay.json'
import mockConvertCurrencies from '../../../__mocks__/Wallets/walletConvertCurrencies.json'
import mockConvertQuote from '../../../__mocks__/Wallets/walletQuote.json'
import mockConvertQuoteWithConversionRateGreaterThanOne from '../../../__mocks__/Wallets/walletQuoteWithConversionRateGreaterThanOne.json'
import mockWallets from '../../../__mocks__/Wallets/wallets.json'
import massPayoutsWallets from '../../../__mocks__/Wallets/wallets.json'
import mockedWallets from '../../../__mocks__/Wallets/wallets.json'
import walletSearchTransactions from '../../../__mocks__/Wallets/walletSearchResponse.json'
import mockedWalletSummary from '../../../__mocks__/Wallets/walletSummary.json'
import mockWalletsWithdrawValidateError from '../../../__mocks__/Wallets/withdrawValidateErrors.json'
import zarMultiplePaymentMethods from '../../../__mocks__/Wallets/zarMultiplePaymentMethods.json'
import mockZarWalletLimits from '../../../__mocks__/Wallets/zarWalletLimits.json'

const API_URL = process.env.API_URL

const postWalletPayHandler = rest.post<WalletsPayRequest, PathParams, WalletsPayRequest>(
  `${API_URL}/api/wallet/send/*`,
  async (req, res, ctx) => {
    return res(ctx.json(mockPay))
  }
)

export const getZarWalletLimitsHandler = rest.get<CurrencyLimits>(
  `${API_URL}/api/v1/limit`,
  async (req, res, ctx) => {
    return res(ctx.json(mockZarWalletLimits), ctx.status(200))
  }
)

export const getZarWalletLimitsWithDelayHandler = rest.get<CurrencyLimits>(
  `${API_URL}/api/v1/limit`,
  async (req, res, ctx) => {
    return res(ctx.delay(250), ctx.json(mockZarWalletLimits), ctx.status(200))
  }
)

export const getZarWalletLimitsHandlerError = rest.get<CurrencyLimits>(
  `${API_URL}/api/v1/limit`,
  async (req, res, ctx) => {
    return res(ctx.status(500), ctx.json({ message: 'Failed to fetch limits', status: 500 }))
  }
)

export const getZarMultiplePaymentMethods = rest.get<DepositBankAccount[]>(
  `${API_URL}/api/v2/wallet/:id/deposit-options`,
  async (req, res, ctx) => {
    return res(ctx.json(zarMultiplePaymentMethods), ctx.status(200))
  }
)

export const getEurSwiftPaymentMethods = rest.get<DepositBankAccount[]>(
  `${API_URL}/api/v2/wallet/:id/deposit-options`,
  async (req, res, ctx) => {
    return res(ctx.json(eurSwiftPaymentMethods), ctx.status(200))
  }
)

const getConvertCurrenciesHandler = rest.get<Currency[]>(
  `${API_URL}/api/wallet/*/transfer`,
  async (req, res, ctx) => {
    return res(ctx.json(mockConvertCurrencies))
  }
)

const postWalletQuoteEstimateHandler = rest.post<{}, PathParams, Quote>(
  `${API_URL}/api/v1/quote`,
  async (req, res, ctx) => {
    return res(ctx.json(mockConvertQuote))
  }
)

export const postWalletQuoteEstimateHandlerError = rest.post<
  {},
  PathParams,
  BaseErrorResponse | Quote
>(`${API_URL}/api/v1/quote`, async (req, res, ctx) => {
  return res(ctx.status(500), ctx.json({ message: 'Failed to estimate quote', status: 500 }))
})

export const postWalletQuoteWithConversionRateGreaterThanOne = rest.post<{}, PathParams, Quote>(
  `${API_URL}/api/v1/quote`,
  async (req, res, ctx) => {
    return res(ctx.json(mockConvertQuoteWithConversionRateGreaterThanOne))
  }
)

export const postWalletQuoteWithConversionRateGreaterThanOneWithDelay = rest.post<
  {},
  PathParams,
  Quote
>(`${API_URL}/api/v1/quote`, async (req, res, ctx) => {
  return res(ctx.delay(250), ctx.json(mockConvertQuoteWithConversionRateGreaterThanOne))
})

const postWalletQuoteHandler = rest.put<{}, PathParams, Quote>(
  `${API_URL}/api/v1/quote/accept/*`,
  async (req, res, ctx) => {
    return res(ctx.json(mockConvertQuote))
  }
)

export const putAcceptQuoteHandlerError = rest.put(
  `${API_URL}/api/v1/quote/accept/*`,
  async (_req, res, ctx) => {
    return res(
      ctx.status(400),
      ctx.json({
        errorList: [
          {
            message: 'Quote has expired',
            code: 'QUOTE_EXPIRED',
          },
        ],
      })
    )
  }
)

export const putAcceptQuoteHandlerWithDelay = rest.put<{}, PathParams, Quote>(
  `${API_URL}/api/v1/quote/accept/*`,
  async (_req, res, ctx) => {
    return res(ctx.delay(500), ctx.json(mockConvertQuote))
  }
)

const getWalletSummary = rest.get<WalletSummary>(
  `${API_URL}/api/wallet/summary/total`,
  async (req, res, ctx) => {
    return res(ctx.json(mockedWalletSummary))
  }
)

export const getWalletPayFee = rest.get<PayFee>(
  `${API_URL}/api/wallet/withdraw/:walletId/fee`,
  async (req, res, ctx) => {
    return res(
      ctx.json({ currencyCode: 'EUR', feePercentage: 0.3, fee: 5, maximumWithdrawalAmount: '100' })
    )
  }
)

export const getMockMassPayoutsWallets = rest.get<WalletType[]>(
  `${API_URL}/api/wallet`,
  async (req, res, ctx) => {
    return res(ctx.json(massPayoutsWallets))
  }
)

export const getAllWallets = rest.get(`${API_URL}/api/wallet`, async (_req, res, ctx) => {
  return res(ctx.json(mockedWallets))
})

const getTransaction = rest.get<Transaction[]>(
  `${API_URL}/api/transaction/*`,
  async (req, res, ctx) => {
    return res(ctx.json(mockedTransactions))
  }
)

const walletsWithdrawValidateHandlerException = rest.post<
  WalletsWithdrawValidateRequest,
  PathParams,
  WalletsWithdrawValidateResponseError
>(`${API_URL}/api/wallet/withdraw/validate/*`, async (req, res, ctx) => {
  return res(ctx.status(400), ctx.json(mockWalletsWithdrawValidateError))
})

export const getWalletById = rest.get<WalletType>(
  `${process.env.API_URL}/api/wallet/:id`,
  async (req, res, ctx) => {
    const { id } = req.params
    const wallet = mockWallets.find(wallet => wallet.id === Number(id))
    if (wallet) {
      return res(ctx.json(wallet))
    }
    return res(ctx.json(mockWallets[0]))
  }
)
export const getWalletByIdNotFoundError = rest.get<WalletType>(
  `${process.env.API_URL}/api/wallet/:id`,
  async (req, res, ctx) => {
    return res(ctx.status(404))
  }
)

export const putChangeWalletDescription = rest.put<WalletType>(
  `${process.env.API_URL}/api/wallet/description/:id`,
  async (req, res, ctx) => {
    return res(ctx.json(mockWallets[0]))
  }
)

export const putChangeWalletDescriptionError = rest.put<WalletType>(
  `${process.env.API_URL}/api/wallet/description/:id`,
  async (req, res, ctx) => {
    return res(ctx.status(400), ctx.json({}))
  }
)

const postDepositDetails = rest.post<WalletType>(
  `${API_URL}/api/wallet/deposit/:id`,
  async (req, res, ctx) => {
    const { id } = req.params
    const wallet = mockWallets.find(wallet => wallet.id === Number(id))
    return res(ctx.json(wallet))
  }
)

export const postCreateWalletCurrencyNotSupportedError = rest.post<WalletType>(
  `${API_URL}/api/wallet`,
  async (req, res, ctx) => {
    return res(
      ctx.status(400),
      ctx.json({
        errorList: [
          {
            code: 'MER-XP-2007',
            status: 'invalid',
            message: 'Currency not supported',
            details: null,
          },
        ],
      })
    )
  }
)

export const postCreateWalletError = rest.post<WalletType>(
  `${API_URL}/api/wallet`,
  async (req, res, ctx) => {
    return res(ctx.status(400), ctx.json('Oops! Something went wrong'))
  }
)

const getSupportedCurrencies = rest.get<CurrencyInterface[]>(
  `${API_URL}/api/currency/deposit`,
  async (req, res, ctx) => {
    return res(ctx.json(mockedSupportedCurrencies))
  }
)

const postWallet = rest.post<WalletType>(`${API_URL}/api/wallet`, async (req, res, ctx) => {
  return res(ctx.json(mockWallets[0]))
})

const sendWalletCsvTransactionExportToEmail = rest.get(
  `${API_URL}/api/transaction/report`,
  async (req, res, ctx) => {
    return res(ctx.json(mockCsvExportTransactions))
  }
)

const sendWalletPdfTransactionExportToEmail = rest.get(
  `${API_URL}/api/transaction/report/external`,
  async (req, res, ctx) => {
    return res(ctx.json(mockPdfExportTransactions))
  }
)

const postWalletSearch = rest.post(
  `${API_URL}/api/search/v1/transaction`,
  async (req, res, ctx) => {
    return res(ctx.json(walletSearchTransactions))
  }
)

export default [
  postWalletPayHandler,
  postWalletQuoteEstimateHandler,
  postWalletQuoteHandler,
  postWalletSearch,
  getConvertCurrenciesHandler,
  getAllWallets,
  getTransaction,
  getWalletSummary,
  walletsWithdrawValidateHandlerException,
  getWalletById,
  getWalletPayFee,
  putChangeWalletDescription,
  postDepositDetails,
  getSupportedCurrencies,
  postWallet,
  sendWalletCsvTransactionExportToEmail,
  sendWalletPdfTransactionExportToEmail,
]
